import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addExam, getInst } from '../../api';
import Swal from 'sweetalert2';
import toast, { Toaster } from 'react-hot-toast';
import { 
  DocumentTextIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CalendarIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const AddExam = () => {
  const navigate = useNavigate();
  const [examDetails, setExamDetails] = useState({
    name: '',
    class: '',
    insid: '',
    estatus: '0',
    duration: '',
    tmarks: '',
    date: '',
    start_time: '',
    end_time: '',
    schedule_enabled: 0,
  });
  const [errors, setErrors] = useState({});
  const [instOptions, setInstOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchInstOptions = async () => {
      try {
        const data = await getInst();
        setInstOptions(data.map(inst => ({ value: inst.id, label: inst.name })));
      } catch (error) {
        console.error('Failed to fetch inst options:', error);
        toast.error('Failed to load institutions');
      } finally {
        setLoading(false);
      }
    };
    fetchInstOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExamDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!examDetails.name.trim()) newErrors.name = 'Exam name is required';
    if (!examDetails.class) newErrors.class = 'Class is required';
    if (!examDetails.insid) newErrors.insid = 'Instructions are required';
    if (!examDetails.duration) newErrors.duration = 'Duration is required';
    if (!examDetails.tmarks) newErrors.tmarks = 'Total marks is required';
    if (!examDetails.date) newErrors.date = 'Date is required';

    if (examDetails.schedule_enabled === 1) {
      if (!examDetails.start_time) {
        newErrors.start_time = 'Start time is required';
      }
      if (!examDetails.end_time) {
        newErrors.end_time = 'End time is required';
      }
      
      if (examDetails.start_time && examDetails.end_time) {
        const startMinutes = timeToMinutes(examDetails.start_time);
        const endMinutes = timeToMinutes(examDetails.end_time);
        
        if (startMinutes === endMinutes) {
          newErrors.end_time = 'End time cannot be the same as start time';
        }
        
        if (endMinutes < startMinutes) {
          console.log('Overnight exam detected: Start ' + examDetails.start_time + ' to ' + examDetails.end_time + ' (next day)');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await addExam(examDetails);
      
      if (response.status === 'success') {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Exam created successfully',
          confirmButtonColor: '#3B82F6',
          customClass: {
            popup: 'rounded-2xl',
            confirmButton: 'rounded-xl px-6 py-2.5'
          }
        }).then(() => {
          navigate('/admin/exam');
        });
      } else {
        toast.error(response.message || 'Failed to create exam');
      }
    } catch (error) {
      console.error('Error creating exam:', error);
      toast.error('An error occurred while creating the exam');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <DocumentTextIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Create New Exam</h1>
              <p className="text-sm text-gray-500">Set up a new examination with details and schedule</p>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          
          {/* Basic Information Section */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <AcademicCapIcon className="h-5 w-5 mr-2 text-blue-600" />
              Basic Information
            </h2>
            
            {/* Exam Name - Full Width */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Exam Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={examDetails.name}
                onChange={handleChange}
                placeholder="Enter exam name (e.g., Mid-Term Mathematics)"
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.name ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors.name && (
                <div className="flex items-center mt-2 text-red-600 text-sm">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.name}
                </div>
              )}
            </div>

            {/* Class, Instructions, Duration, Total Marks - 4 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Class <span className="text-red-500">*</span>
                </label>
                <select
                  name="class"
                  value={examDetails.class}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.class ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <option value="">Select Class</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(cls => (
                    <option key={cls} value={cls}>Class {cls}</option>
                  ))}
                </select>
                {errors.class && (
                  <div className="flex items-center mt-2 text-red-600 text-sm">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.class}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Instructions <span className="text-red-500">*</span>
                </label>
                <select
                  name="insid"
                  value={examDetails.insid}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.insid ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <option value="">Select Instructions</option>
                  {instOptions.map(inst => (
                    <option key={inst.value} value={inst.value}>{inst.label}</option>
                  ))}
                </select>
                {errors.insid && (
                  <div className="flex items-center mt-2 text-red-600 text-sm">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.insid}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Duration (min) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="duration"
                  value={examDetails.duration}
                  onChange={handleChange}
                  placeholder="60"
                  min="1"
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.duration ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.duration && (
                  <div className="flex items-center mt-2 text-red-600 text-sm">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.duration}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Total Marks <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="tmarks"
                  value={examDetails.tmarks}
                  onChange={handleChange}
                  placeholder="100"
                  min="1"
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.tmarks ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.tmarks && (
                  <div className="flex items-center mt-2 text-red-600 text-sm">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.tmarks}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
              Schedule
            </h2>

            {/* Exam Date */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Exam Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={examDetails.date}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.date ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors.date && (
                <div className="flex items-center mt-2 text-red-600 text-sm">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.date}
                </div>
              )}
            </div>

            {/* Time Slot Scheduling Toggle */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="schedule_enabled"
                  checked={examDetails.schedule_enabled === 1}
                  onChange={handleChange}
                  className="mt-1 mr-3 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="font-semibold text-gray-800">Enable Time Slot Scheduling</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Auto publish/unpublish exam during specified time slot
                  </p>
                </div>
              </label>
              
              {examDetails.schedule_enabled === 1 && (
                <>
                  <div className="mt-4 p-4 bg-blue-100 border border-blue-300 rounded-lg">
                    <p className="text-sm text-blue-800 flex items-start">
                      <ExclamationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                      <span>The exam will be automatically published only during the specified time slot on the exam date.</span>
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="start_time"
                        value={examDetails.start_time}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.start_time ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.start_time && (
                        <div className="flex items-center mt-2 text-red-600 text-sm">
                          <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                          {errors.start_time}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="end_time"
                        value={examDetails.end_time}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.end_time ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.end_time && (
                        <div className="flex items-center mt-2 text-red-600 text-sm">
                          <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                          {errors.end_time}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status Section */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Publication Status</h2>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Status
              </label>
              <select
                name="estatus"
                value={examDetails.estatus}
                onChange={handleChange}
                disabled={examDetails.schedule_enabled === 1}
                className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  examDetails.schedule_enabled === 1 ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value="0">📝 Unpublished (Draft)</option>
                <option value="1">✅ Published (Live)</option>
              </select>
              {examDetails.schedule_enabled === 1 && (
                <p className="text-gray-500 text-sm mt-2 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Status is controlled by time slot scheduling
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3.5 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <CheckCircleIcon className="h-5 w-5" />
              <span>{submitting ? 'Creating Exam...' : 'Create Exam'}</span>
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/admin/exam')}
              disabled={submitting}
              className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-6 py-3.5 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300 disabled:opacity-50"
            >
              <XMarkIcon className="h-5 w-5" />
              <span>Cancel</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExam;
